import { asRecord } from "../blocks/content-helpers";
import type { BlockGateInteractionKind } from "../blocks/define-block-kind";
import { BLOCK_KIND_REGISTRY, isBlockKindType } from "../blocks/registry";
import {
	type MotionPlayback,
	parseMotionSettings,
	type ResolvedMotionField,
	resolveMotionPlayback,
	resolveMotionSettings,
} from "../motion";
import {
	groupCuesIntoBlockWindows,
	type NarrationCue,
	resolveSegmentMotionSchedule,
} from "./motion-schedule";
import { msToDurationMinutes } from "./reading-rate";
import {
	resolveTopicSegmentContentMs,
	type TopicDwellBlockInput,
	type TopicTimelineSegmentKind,
} from "./segment-duration";
import { msToFrame, TOPIC_TIMELINE_FPS } from "./time";

/** Mirrors Prisma `TopicContinuationTimerPolicy`. */
export const TOPIC_CONTINUATION_TIMER_POLICIES = [
	"CALCULATED",
	"FIXED",
	"NONE",
] as const;
export type TopicContinuationTimerPolicy =
	(typeof TOPIC_CONTINUATION_TIMER_POLICIES)[number];

/** Kinds that hold no motion window of their own (they still count toward dwell and gates). */
const UNSCHEDULED_BLOCK_KINDS: ReadonlySet<string> = new Set(["DIVIDER"]);

export interface TopicTimelineBlockInput extends TopicDwellBlockInput {
	id: string;
	orderIndex: number;
	/** Raw `Block.motionSettings`. */
	motionSettings?: unknown;
}

export interface TopicTimelineCardInput {
	id: string;
	kind: TopicTimelineSegmentKind;
	/** Content order; cover always first and close always last. */
	orderIndex?: number;
	/** Raw `Card.motionSettings`. */
	motionSettings?: unknown;
	blocks?: readonly TopicTimelineBlockInput[];
	/** Body + summary narration length when known; never includes trailing silence. */
	narrationContentMs?: number | null;
	/** Parsed narration cues; they place block motion windows when present. */
	narrationCues?: readonly NarrationCue[] | null;
	/** Cover / close speakable text. */
	bookendSpeakableText?: string | null;
}

export interface CompileTopicTimelineInput {
	cards: readonly TopicTimelineCardInput[];
	/** Raw `Topic.motionSettings`. */
	topicMotionSettings?: unknown;
	continuationTimerPolicy?: TopicContinuationTimerPolicy | null;
	continuationTimerFixedMs?: number | null;
	/** Pause after each segment; carried for the player, never added to durations. */
	segmentTrailingSilenceMs?: number | null;
	preferReducedMotion?: boolean;
	fps?: number;
}

export interface TopicTimelinePlayback extends MotionPlayback {
	durationFrames: number;
}

export interface TopicTimelineBlockV1 {
	blockId: string;
	/** Window bounds within the segment (ms from segment start). */
	cueStartMs: number;
	cueEndMs: number;
	fromMs: number;
	inMs: number;
	holdMs: number;
	outMs: number;
	/** Absolute frames (from the start of the topic). */
	fromFrame: number;
	inFrames: number;
	holdFrames: number;
	outFrames: number;
	animationIn: MotionPlayback;
	animationOut: MotionPlayback;
}

export interface TopicTimelineGateInteractionV1 {
	blockId: string;
	kind: BlockGateInteractionKind;
	/** The question a `quiz.answer` requires (one per question of the QUIZ block). */
	questionId?: string;
}

export interface TopicTimelineGateV1 {
	policy: TopicContinuationTimerPolicy;
	/** Foreground time a learner must spend on the segment before it unlocks. */
	minDwellMs: number;
	/** Interactions that must be recorded before it unlocks. */
	interactions: TopicTimelineGateInteractionV1[];
}

export interface TopicTimelineSegmentV1 {
	cardId: string;
	kind: TopicTimelineSegmentKind;
	index: number;
	startMs: number;
	endMs: number;
	durationMs: number;
	startFrame: number;
	durationInFrames: number;
	/** Pause after the segment (player), outside `durationMs`. */
	trailingSilenceMs: number;
	transitionIn: TopicTimelinePlayback;
	transitionOut: TopicTimelinePlayback;
	/** Content segments only; block motion windows in block order. */
	blocks: TopicTimelineBlockV1[];
	gate: TopicTimelineGateV1;
}

/** The single time model every format projects (R13): durations, frames, motion and gates. */
export interface TopicTimelineV1 {
	version: 1;
	fps: number;
	preferReducedMotion: boolean;
	totalMs: number;
	totalFrames: number;
	segments: TopicTimelineSegmentV1[];
}

const SEGMENT_KIND_ORDER: Record<TopicTimelineSegmentKind, number> = {
	cover: 0,
	content: 1,
	close: 2,
};

function orderCards(
	cards: readonly TopicTimelineCardInput[],
): TopicTimelineCardInput[] {
	return cards
		.map((card, inputIndex) => ({ card, inputIndex }))
		.sort(
			(a, b) =>
				SEGMENT_KIND_ORDER[a.card.kind] -
					SEGMENT_KIND_ORDER[b.card.kind] ||
				(a.card.orderIndex ?? 0) - (b.card.orderIndex ?? 0) ||
				a.inputIndex - b.inputIndex,
		)
		.map(({ card }) => card);
}

function playback(
	field: ResolvedMotionField,
	role: MotionPlayback["role"],
	reducedMotion: boolean,
): MotionPlayback {
	return resolveMotionPlayback({
		role,
		kindKey: field.kindKey,
		durationMs: field.durationMs,
		params: field.params,
		reducedMotion,
	});
}

function normalizePolicy(
	value: TopicContinuationTimerPolicy | null | undefined,
): TopicContinuationTimerPolicy {
	return value === "FIXED" || value === "NONE" ? value : "CALCULATED";
}

/** Bookends unlock instantly; FIXED without a positive length falls back to CALCULATED. */
function resolveGate(
	card: TopicTimelineCardInput,
	durationMs: number,
	input: CompileTopicTimelineInput,
): TopicTimelineGateV1 {
	const interactions: TopicTimelineGateInteractionV1[] = [];
	for (const block of card.blocks ?? []) {
		const key = block.type.toUpperCase();
		if (!isBlockKindType(key)) {
			continue;
		}
		for (const interaction of BLOCK_KIND_REGISTRY[key].gate(
			asRecord(block.content),
		)) {
			interactions.push(
				interaction.kind === "quiz.answer"
					? {
							blockId: block.id,
							kind: interaction.kind,
							questionId: interaction.questionId,
						}
					: { blockId: block.id, kind: interaction.kind },
			);
		}
	}
	if (card.kind !== "content") {
		return { policy: "NONE", minDwellMs: 0, interactions };
	}
	const policy = normalizePolicy(input.continuationTimerPolicy);
	if (policy === "NONE") {
		return { policy, minDwellMs: 0, interactions };
	}
	const fixedMs = input.continuationTimerFixedMs;
	if (policy === "FIXED" && typeof fixedMs === "number" && fixedMs > 0) {
		return { policy, minDwellMs: Math.round(fixedMs), interactions };
	}
	return { policy: "CALCULATED", minDwellMs: durationMs, interactions };
}

/**
 * Compile a topic's timeline (R13). Pure and deterministic: the same input always yields the same
 * JSON, in the browser, the api and the bake.
 */
export function compileTopicTimeline(
	input: CompileTopicTimelineInput,
): TopicTimelineV1 {
	const fps = input.fps ?? TOPIC_TIMELINE_FPS;
	const preferReducedMotion = input.preferReducedMotion === true;
	const trailingSilenceMs = Math.max(
		0,
		Math.round(input.segmentTrailingSilenceMs ?? 0),
	);
	const topicLayer = parseMotionSettings(input.topicMotionSettings);

	const segments: TopicTimelineSegmentV1[] = [];
	let cursorMs = 0;
	let cursorFrame = 0;

	for (const card of orderCards(input.cards)) {
		const sortedBlocks = [...(card.blocks ?? [])].sort(
			(a, b) => a.orderIndex - b.orderIndex,
		);
		const durationMs = Math.max(
			1,
			Math.round(
				resolveTopicSegmentContentMs({
					kind: card.kind,
					narrationContentMs: card.narrationContentMs,
					blocks: sortedBlocks,
					bookendSpeakableText: card.bookendSpeakableText,
				}),
			),
		);
		const durationInFrames = Math.max(1, msToFrame(durationMs, fps));

		const cardLayer = parseMotionSettings(card.motionSettings);
		const cardMotion = resolveMotionSettings({
			topic: topicLayer,
			card: cardLayer,
		});
		const transitionIn = playback(
			cardMotion.cardIn,
			"transition",
			preferReducedMotion,
		);
		const transitionOut = playback(
			cardMotion.cardOut,
			"transition",
			preferReducedMotion,
		);

		const blocks: TopicTimelineBlockV1[] = [];
		if (card.kind === "content") {
			const scheduled = sortedBlocks
				.filter(
					(block) =>
						!UNSCHEDULED_BLOCK_KINDS.has(block.type.toUpperCase()),
				)
				.map((block) => {
					// Block in/out: registry → topic → card → block (R11).
					const resolved = resolveMotionSettings({
						topic: topicLayer,
						card: cardLayer,
						block: parseMotionSettings(block.motionSettings),
					});
					return {
						blockId: block.id,
						animationIn: playback(
							resolved.blockIn,
							"animation",
							preferReducedMotion,
						),
						animationOut: playback(
							resolved.blockOut,
							"animation",
							preferReducedMotion,
						),
					};
				});
			const schedule = resolveSegmentMotionSchedule({
				contentMs: durationMs,
				cardTransitionInMs: transitionIn.durationMs,
				cardTransitionOutMs: transitionOut.durationMs,
				blocks: scheduled.map((entry) => ({
					blockId: entry.blockId,
					inMs: entry.animationIn.durationMs,
					outMs: entry.animationOut.durationMs,
				})),
				blockCueWindows: groupCuesIntoBlockWindows({
					cues: card.narrationCues,
					blockIds: scheduled.map((entry) => entry.blockId),
					segmentMs: durationMs,
				}),
			});
			for (const [index, window] of schedule.blocks.entries()) {
				const entry = scheduled[index];
				if (!entry) {
					continue;
				}
				blocks.push({
					blockId: window.blockId,
					cueStartMs: window.cueStartMs,
					cueEndMs: window.cueEndMs,
					fromMs: window.fromMs,
					inMs: window.inMs,
					holdMs: window.holdMs,
					outMs: window.outMs,
					fromFrame: cursorFrame + msToFrame(window.fromMs, fps),
					inFrames: msToFrame(window.inMs, fps),
					holdFrames: msToFrame(window.holdMs, fps),
					outFrames: msToFrame(window.outMs, fps),
					animationIn: entry.animationIn,
					animationOut: entry.animationOut,
				});
			}
		}

		segments.push({
			cardId: card.id,
			kind: card.kind,
			index: segments.length,
			startMs: cursorMs,
			endMs: cursorMs + durationMs,
			durationMs,
			startFrame: cursorFrame,
			durationInFrames,
			trailingSilenceMs,
			transitionIn: {
				...transitionIn,
				durationFrames: msToFrame(transitionIn.durationMs, fps),
			},
			transitionOut: {
				...transitionOut,
				durationFrames: msToFrame(transitionOut.durationMs, fps),
			},
			blocks,
			gate: resolveGate(card, durationMs, input),
		});
		cursorMs += durationMs;
		cursorFrame += durationInFrames;
	}

	return {
		version: 1,
		fps,
		preferReducedMotion,
		totalMs: cursorMs,
		totalFrames: cursorFrame,
		segments,
	};
}

/** The segment for a card; `undefined` when the card is not on the timeline. */
export function findTopicTimelineSegment(
	timeline: TopicTimelineV1,
	cardId: string,
): TopicTimelineSegmentV1 | undefined {
	return timeline.segments.find((segment) => segment.cardId === cardId);
}

/** Cached `durationMinutes` (F3): every segment plus its trailing pause; null for an empty timeline. */
export function topicTimelineDurationMinutes(
	timeline: TopicTimelineV1,
): number | null {
	return msToDurationMinutes(
		timeline.segments.reduce(
			(total, segment) =>
				total + segment.durationMs + segment.trailingSilenceMs,
			0,
		),
	);
}
